import React from 'react'

import { EditableFieldProps } from '../core-field/editable-field-json-props'
import { IPulseHighlight } from '../../behavior/pulse-highlight'

export type MaskStoredAs = 'raw' | 'formatted'
export type AutoCapitalize = 'none' | 'characters' | 'words'

/**
 * Default placeholder shown when the caller does not provide one:
 * "Enter <label>", or "Enter value" if there is no label.
 */
export function getDefaultInputPlaceholder(label?: string): string {
  const trimmed = label?.trim()
  return trimmed ? `Enter ${trimmed}` : 'Enter value'
}

/**
 * Synchronously formats a raw value against the legacy text-input-mask
 * pattern (`+1 ([000]) [000]-[0000]` etc.). Placeholder tokens:
 *   0 required digit, 9 optional digit, A required letter, a optional letter, _ any char.
 * Returns the mask-formatted string, or `undefined` if the value cannot
 * fill enough of the mask to be legibly formatted — callers should fall
 * back to the raw stored value in that case.
 */
export function applyTextMask(textMask: string, rawValue: string): string | undefined {
  if (!textMask || rawValue == null) return undefined
  let result = ''
  let vi = 0
  let requiredFilled = 0
  let requiredTotal = 0
  let i = 0
  while (i < textMask.length) {
    if (textMask[i] === '[') {
      const end = textMask.indexOf(']', i)
      if (end === -1) break
      const group = textMask.slice(i + 1, end)
      for (const ch of group) {
        const isRequired = ch === '0' || ch === 'A'
        if (isRequired) requiredTotal += 1
        if (vi >= rawValue.length) continue
        const next = rawValue[vi]
        const matches =
          (ch === '0' || ch === '9') ? /[0-9]/.test(next) :
          (ch === 'A' || ch === 'a') ? /[a-zA-Z]/.test(next) :
          ch === '_' ? true :
          false
        if (!matches) return undefined
        result += next
        vi += 1
        if (isRequired) requiredFilled += 1
      }
      i = end + 1
    } else {
      if (vi < rawValue.length) result += textMask[i]
      i += 1
    }
  }
  if (vi < rawValue.length) return undefined
  if (requiredTotal > 0 && requiredFilled === 0) return undefined
  return result
}

export interface UI2InputFieldProps extends EditableFieldProps<string> {
  /** Display/serialization format string (e.g. number formats). */
  format?: string
  /** Text mask pattern; `maskStoredAs` controls what gets stored. */
  textMask?: string
  /** 'raw' strips mask literals before storing; 'formatted' stores as displayed. Default: 'raw'. */
  maskStoredAs?: MaskStoredAs
  /** Blur replaces the displayed value with asterisks. */
  obscureOnBlur?: boolean
  rightContent?: React.ReactNode
  leftContent?: React.ReactNode
  /** HTML/RN input type (e.g. 'text', 'email', 'tel', 'password', 'number'). */
  type?: string
  /** HTML `inputMode` hint (DW). Falls back to a type-derived default. */
  inputMode?: string
  /** Autocapitalization behavior for keyboard-driven capitalization. */
  autoCapitalize?: AutoCapitalize
  /** HTML `autocomplete` attribute (DW). */
  autoComplete?: string
  /** Maximum accepted character count. */
  maxLength?: number
  autoFocus?: boolean
  /**
   * Parent-owned pulse ref; the field triggers `pulse()` after a value is
   * confirmed. Wraps only the input body — never the label or help text.
   *
   * Same prop the shell reads (`FieldShellProps.pulseRef`): passing it here
   * also makes this ring the one the focus registry pulses, so the field
   * ends up with exactly one ring rather than a parent's and the shell's.
   */
  pulseRef?: React.RefObject<IPulseHighlight>
}
